<template>
  <div class="home-page portal-shell">
    <header class="hero-section">
      <div class="hero-grid"></div>
      <div class="hero-orbit hero-orbit-a"></div>
      <div class="hero-orbit hero-orbit-b"></div>

      <div class="hero-content">
        <div class="topbar">
          <div class="brand-block">
            <div class="brand-mark">
              <i class="el-icon-cpu"></i>
            </div>
            <div>
              <h1 class="site-title">胡工技术分析</h1>
              <p class="site-subtitle">专注AI前沿资讯 · 洞察技术发展趋势</p>
            </div>
          </div>

          <div class="header-actions">
            <el-input
              v-model="searchKeyword"
              :placeholder="isMobile ? '搜索...' : '搜索AI资讯...'"
              prefix-icon="el-icon-search"
              class="search-input"
              clearable
              @keyup.enter.native="handleSearch"
              @clear="loadArticles"
            />
            <button class="admin-link" type="button" @click="$router.push('/login')">
              <i class="el-icon-setting"></i>
              <span>管理</span>
            </button>
          </div>
        </div>

        <nav class="nav-bar" aria-label="主导航">
          <button
            v-for="item in navItems"
            :key="item"
            :class="['nav-item', { active: item === '首页' }]"
            type="button"
          >
            {{ item }}
          </button>
        </nav>

        <div class="hero-copy">
          <p class="eyebrow">AI INTELLIGENCE PORTAL</p>
          <h2>追踪 AI 前沿动态，沉淀技术趋势判断</h2>
          <p>聚合大模型、AI 应用、RAG、Agent 与国产 AI 生态资讯，形成更适合技术决策阅读的情报流。</p>
        </div>
      </div>
    </header>

    <main class="main-content">
      <div class="content-container">
        <section class="category-bar" aria-label="文章分类">
          <button
            v-for="cat in displayCategories"
            :key="cat.value"
            :class="['category-pill', { active: activeCategory === cat.value }]"
            type="button"
            @click="selectCategory(cat.value)"
          >
            <i :class="cat.icon"></i>
            <span>{{ cat.label }}</span>
          </button>
        </section>

        <section class="stats-bar glass-panel" aria-label="站点统计">
          <div class="stat-item">
            <i class="el-icon-document"></i>
            <span>共 <strong>{{ articles.length }}</strong> 篇文章</span>
          </div>
          <div class="stat-item">
            <i class="el-icon-view"></i>
            <span>总阅读 <strong>{{ totalViews }}</strong></span>
          </div>
          <div class="stat-item accent">
            <i class="el-icon-data-analysis"></i>
            <span>趋势跟踪 <strong>{{ activeCategory || '全部' }}</strong></span>
          </div>
        </section>

        <div v-loading="loading" class="article-grid">
          <article
            v-for="(article, index) in articles"
            :key="article.id"
            :class="['article-card', 'glass-panel', { 'featured-card': index === 0 }]"
            @click="goDetail(article.id)"
          >
            <div class="card-visual" aria-hidden="true">
              <span></span>
            </div>
            <div class="card-header">
              <span class="category-badge">{{ article.category || 'AI资讯' }}</span>
              <span class="card-date">{{ formatDate(article.createdAt) }}</span>
            </div>
            <h3 class="card-title">{{ article.title }}</h3>
            <p class="card-summary">{{ article.summary }}</p>
            <div class="card-footer">
              <span><i class="el-icon-user"></i>{{ article.author || '胡工' }}</span>
              <span><i class="el-icon-view"></i>{{ article.viewCount || 0 }}</span>
            </div>
          </article>
        </div>

        <el-empty v-if="!loading && articles.length === 0" description="暂无资讯文章"></el-empty>
      </div>
    </main>

    <footer class="site-footer">
      <div class="content-container">
        <p>© 2026 胡工技术分析 - AI前沿资讯平台 | <a href="https://beian.miit.gov.cn/" target="_blank">粤ICP备2026030599号-2</a></p>
      </div>
    </footer>
  </div>
</template>

<script>
import api from '../api'

const categoryIcons = {
  全部: 'el-icon-menu',
  AI应用: 'el-icon-connection',
  AI资讯: 'el-icon-news',
  大模型: 'el-icon-cpu',
  深度学习: 'el-icon-data-line',
  国产AI: 'el-icon-place',
  'AI Agent': 'el-icon-guide',
  RAG技术: 'el-icon-collection'
}

const fallbackArticles = [
  {
    id: 1,
    category: 'AI应用',
    title: '具身智能数据战：群核建道场，百度铺管道，京东搭舞台',
    summary: '在具身智能竞赛进入下半场的当下，数据成为新的战略高地。三者路径各异，却共同指向用数据驱动智能体走向真实世界。',
    author: '胡工',
    createdAt: '2026-04-25',
    viewCount: 1024
  },
  {
    id: 2,
    category: 'AI资讯',
    title: 'AI幻觉越来越多，「智障率」已经让人无法容忍了',
    summary: '模型能力快速增长的同时，幻觉、错答和不稳定输出正在成为产品化落地中必须正视的问题。',
    author: '胡工',
    createdAt: '2026-04-25',
    viewCount: 856
  },
  {
    id: 3,
    category: 'AI应用',
    title: '68亿，具身机器人史上最大采购来了',
    summary: '大规模采购信号背后，是机器人产业从技术展示走向场景部署的关键转折。',
    author: '胡工',
    createdAt: '2026-04-25',
    viewCount: 723
  },
  {
    id: 4,
    category: '深度学习',
    title: 'AI科学家还在刷静态榜？基准主动反击，重塑自动科研评价标准',
    summary: '静态榜单越来越难评估真实科研能力，主动式基准正在推动自动科研评价标准升级。',
    author: '胡工',
    createdAt: '2026-04-25',
    viewCount: 612
  },
  {
    id: 5,
    category: 'AI资讯',
    title: '谷歌这把「香蕉」太狠了，何恺明等引爆视觉Transformer时刻',
    summary: '视觉模型的通才化趋势继续增强，图像生成器与视觉学习者之间的边界正在变得模糊。',
    author: '胡工',
    createdAt: '2026-04-25',
    viewCount: 589
  },
  {
    id: 6,
    category: '大模型',
    title: 'Google DeepMind推出Vision Banana：图像生成器即通才视觉学习者',
    summary: '新一代视觉系统把生成、理解和迁移能力整合到统一框架中，打开多模态模型的新空间。',
    author: '胡工',
    createdAt: '2026-04-25',
    viewCount: 541
  }
]

export default {
  name: 'Home',
  data() {
    return {
      articles: [],
      categories: [],
      activeCategory: '',
      searchKeyword: '',
      loading: false,
      isMobile: false,
      navItems: ['首页', 'AI资讯', '深度分析', '大模型', 'AI应用', 'RAG技术', '国产AI', 'AI Agent']
    }
  },
  computed: {
    totalViews() {
      return this.articles.reduce((sum, a) => sum + (a.viewCount || 0), 0)
    },
    displayCategories() {
      const defaults = ['AI应用', 'AI资讯', '大模型', '深度学习', '国产AI', 'AI Agent', 'RAG技术']
      const merged = Array.from(new Set([...(this.categories || []), ...defaults])).filter(Boolean)
      return [
        { label: '全部', value: '', icon: categoryIcons['全部'] },
        ...merged.map(label => ({
          label,
          value: label,
          icon: categoryIcons[label] || 'el-icon-price-tag'
        }))
      ]
    }
  },
  created() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    this.loadArticles()
    this.loadCategories()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    async loadArticles() {
      this.loading = true
      try {
        const params = {}
        if (this.activeCategory) params.category = this.activeCategory
        if (this.searchKeyword) params.keyword = this.searchKeyword
        const { data } = await api.getArticles(params)
        this.articles = Array.isArray(data) ? data : []
      } catch {
        const keyword = this.searchKeyword.trim()
        this.articles = fallbackArticles.filter(article => {
          const matchCategory = !this.activeCategory || article.category === this.activeCategory
          const matchKeyword = !keyword || `${article.title}${article.summary}`.includes(keyword)
          return matchCategory && matchKeyword
        })
      } finally {
        this.loading = false
      }
    },
    async loadCategories() {
      try {
        const { data } = await api.getCategories()
        this.categories = Array.isArray(data) ? data : []
      } catch {
        this.categories = ['AI应用', 'AI资讯', '大模型', '深度学习']
      }
    },
    selectCategory(cat) {
      this.activeCategory = cat
      this.loadArticles()
    },
    handleSearch() {
      this.activeCategory = ''
      this.loadArticles()
    },
    goDetail(id) {
      this.$router.push(`/article/${id}`)
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.portal-shell {
  min-height: 100vh;
  background:
    radial-gradient(circle at 18% 12%, rgba(47, 140, 255, 0.28), transparent 28%),
    radial-gradient(circle at 82% 8%, rgba(79, 220, 255, 0.16), transparent 26%),
    radial-gradient(circle at 75% 46%, rgba(245, 185, 79, 0.1), transparent 22%),
    linear-gradient(180deg, #030814 0%, #07111f 45%, #030814 100%);
  color: #f4f8ff;
}

.hero-section {
  position: relative;
  overflow: hidden;
  min-height: 360px;
  border-bottom: 1px solid rgba(116, 170, 255, 0.18);
}

.hero-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(116, 170, 255, 0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(116, 170, 255, 0.08) 1px, transparent 1px);
  background-size: 42px 42px;
  mask-image: linear-gradient(180deg, rgba(0, 0, 0, 0.9), transparent);
}

.hero-orbit {
  position: absolute;
  border: 1px solid rgba(79, 220, 255, 0.16);
  border-radius: 50%;
  box-shadow: 0 0 70px rgba(47, 140, 255, 0.16);
}

.hero-orbit-a {
  width: 420px;
  height: 420px;
  right: 8%;
  top: -180px;
}

.hero-orbit-b {
  width: 240px;
  height: 240px;
  left: 11%;
  bottom: -130px;
}

.hero-content,
.content-container {
  position: relative;
  max-width: 1480px;
  margin: 0 auto;
  padding: 0 32px;
}

.hero-content {
  padding-top: 34px;
  padding-bottom: 44px;
}

.topbar,
.header-actions,
.brand-block,
.nav-bar,
.stats-bar,
.card-header,
.card-footer {
  display: flex;
  align-items: center;
}

.topbar {
  justify-content: space-between;
  gap: 28px;
}

.brand-block {
  gap: 16px;
  min-width: 0;
}

.brand-mark {
  width: 66px;
  height: 66px;
  display: grid;
  place-items: center;
  border-radius: 20px;
  color: #4fdcff;
  font-size: 36px;
  background: linear-gradient(145deg, rgba(47, 140, 255, 0.22), rgba(79, 220, 255, 0.08));
  border: 1px solid rgba(116, 170, 255, 0.34);
  box-shadow: 0 0 36px rgba(47, 140, 255, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.site-title {
  margin: 0 0 6px;
  color: #f4f8ff;
  font-size: 38px;
  font-weight: 800;
  line-height: 1.1;
}

.site-subtitle {
  margin: 0;
  color: #aebbd0;
  font-size: 16px;
}

.header-actions {
  gap: 14px;
}

.search-input {
  width: 360px;
}

.search-input >>> .el-input__inner {
  height: 48px;
  color: #f4f8ff;
  background: rgba(9, 20, 36, 0.72);
  border: 1px solid rgba(116, 170, 255, 0.28);
  border-radius: 16px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07);
}

.search-input >>> .el-input__inner::placeholder {
  color: #7f8ca3;
}

.search-input >>> .el-input__inner:focus {
  border-color: rgba(79, 220, 255, 0.72);
  box-shadow: 0 0 0 4px rgba(47, 140, 255, 0.12);
}

.admin-link,
.nav-item,
.category-pill {
  border: 1px solid rgba(116, 170, 255, 0.24);
  color: #dbe8ff;
  background: rgba(9, 20, 36, 0.58);
  cursor: pointer;
  transition: transform 0.22s ease, border-color 0.22s ease, background 0.22s ease;
}

.admin-link {
  height: 48px;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 16px;
}

.admin-link:hover,
.nav-item:hover,
.category-pill:hover {
  transform: translateY(-2px);
  border-color: rgba(79, 220, 255, 0.62);
  background: rgba(47, 140, 255, 0.15);
}

.nav-bar {
  gap: 10px;
  margin-top: 28px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
}

.nav-bar::-webkit-scrollbar,
.category-bar::-webkit-scrollbar {
  display: none;
}

.nav-item {
  flex: 0 0 auto;
  height: 40px;
  padding: 0 16px;
  border-radius: 999px;
  font-size: 14px;
}

.nav-item.active {
  color: #ffffff;
  border-color: rgba(79, 220, 255, 0.72);
  background: linear-gradient(135deg, rgba(47, 140, 255, 0.44), rgba(79, 220, 255, 0.18));
  box-shadow: 0 0 24px rgba(47, 140, 255, 0.2);
}

.hero-copy {
  max-width: 760px;
  margin-top: 52px;
}

.eyebrow {
  margin: 0 0 12px;
  color: #f5b94f;
  font-size: 13px;
  font-weight: 700;
}

.hero-copy h2 {
  margin: 0;
  color: #ffffff;
  font-size: 46px;
  line-height: 1.16;
  font-weight: 800;
}

.hero-copy p:last-child {
  max-width: 680px;
  margin: 18px 0 0;
  color: #aebbd0;
  font-size: 17px;
  line-height: 1.8;
}

.main-content {
  padding: 32px 0 28px;
}

.category-bar {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 12px;
  scrollbar-width: none;
}

.category-pill {
  flex: 0 0 auto;
  height: 42px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  border-radius: 999px;
  font-size: 14px;
}

.category-pill.active {
  color: #ffffff;
  border-color: rgba(79, 220, 255, 0.7);
  background: rgba(47, 140, 255, 0.28);
  box-shadow: 0 0 26px rgba(47, 140, 255, 0.22);
}

.glass-panel {
  background: rgba(9, 20, 36, 0.72);
  border: 1px solid rgba(116, 170, 255, 0.22);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.36), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(18px);
}

.stats-bar {
  justify-content: space-between;
  gap: 18px;
  margin: 14px 0 26px;
  padding: 18px 22px;
  border-radius: 18px;
}

.stat-item {
  gap: 10px;
  color: #aebbd0;
  font-size: 15px;
}

.stat-item i {
  color: #4fdcff;
  font-size: 18px;
}

.stat-item strong {
  color: #4fdcff;
  font-size: 22px;
}

.stat-item.accent i,
.stat-item.accent strong {
  color: #f5b94f;
}

.article-grid {
  min-height: 320px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
}

.article-card {
  position: relative;
  overflow: hidden;
  min-height: 310px;
  padding: 24px;
  border-radius: 18px;
  cursor: pointer;
  transition: transform 0.24s ease, border-color 0.24s ease, box-shadow 0.24s ease;
}

.article-card:hover {
  transform: translateY(-4px);
  border-color: rgba(79, 220, 255, 0.56);
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.42), 0 0 36px rgba(47, 140, 255, 0.16);
}

.featured-card {
  grid-column: span 2;
  min-height: 360px;
}

.card-visual {
  height: 84px;
  margin: -4px 0 18px;
  border-radius: 14px;
  background:
    radial-gradient(circle at 16% 40%, rgba(79, 220, 255, 0.54), transparent 8%),
    radial-gradient(circle at 52% 48%, rgba(245, 185, 79, 0.35), transparent 7%),
    linear-gradient(135deg, rgba(47, 140, 255, 0.26), rgba(11, 24, 42, 0.62));
  border: 1px solid rgba(116, 170, 255, 0.2);
}

.card-visual span {
  display: block;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(115deg, transparent 0 16px, rgba(255, 255, 255, 0.08) 17px 18px);
}

.card-header,
.card-footer {
  justify-content: space-between;
  gap: 14px;
}

.category-badge {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 9px;
  color: #04101d;
  font-size: 13px;
  font-weight: 700;
  background: linear-gradient(135deg, #4fdcff, #2f8cff);
}

.card-date,
.card-footer {
  color: #7f8ca3;
  font-size: 13px;
}

.card-title {
  margin: 18px 0 12px;
  color: #f4f8ff;
  font-size: 24px;
  line-height: 1.42;
  font-weight: 800;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.featured-card .card-title {
  max-width: 840px;
  font-size: 30px;
}

.card-summary {
  margin: 0 0 22px;
  color: #aebbd0;
  font-size: 15px;
  line-height: 1.8;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-footer {
  margin-top: auto;
  padding-top: 18px;
  border-top: 1px solid rgba(116, 170, 255, 0.16);
}

.card-footer span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.site-footer {
  padding: 28px 0 36px;
  color: #7f8ca3;
  text-align: center;
  font-size: 13px;
}

.site-footer a {
  color: #4fdcff;
}

@media screen and (max-width: 1180px) {
  .topbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
  }

  .search-input {
    flex: 1;
    width: auto;
  }

  .article-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 768px) {
  .hero-section {
    min-height: auto;
  }

  .hero-content,
  .content-container {
    padding-left: 16px;
    padding-right: 16px;
  }

  .hero-content {
    padding-top: 22px;
    padding-bottom: 30px;
  }

  .brand-mark {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    font-size: 28px;
  }

  .site-title {
    font-size: 25px;
  }

  .site-subtitle {
    font-size: 13px;
  }

  .header-actions {
    gap: 10px;
  }

  .admin-link {
    width: 48px;
    padding: 0;
    justify-content: center;
  }

  .admin-link span {
    display: none;
  }

  .hero-copy {
    margin-top: 34px;
  }

  .hero-copy h2 {
    font-size: 30px;
  }

  .hero-copy p:last-child {
    font-size: 15px;
  }

  .stats-bar {
    align-items: flex-start;
    flex-direction: column;
  }

  .article-grid {
    grid-template-columns: 1fr;
  }

  .featured-card {
    grid-column: span 1;
  }

  .article-card,
  .featured-card {
    min-height: auto;
    padding: 20px;
  }

  .card-title,
  .featured-card .card-title {
    font-size: 22px;
  }
}
</style>
